// web/src/routes/Dashboard.tsx
// The front page of the magazine. Order of importance, top→bottom:
//   1. Onboarding card (if not dismissed)
//   2. Masthead band — wordmark + dated issue line, full-bleed
//   3. Top story — featured MatchCard of the most-recent match across all kids
//   4. Kids strip — KidAvatars horizontally with name + squad number
//   (Season-aggregate tiles removed 2026-05-19 — per-kid Stats page owns
//    that view. The home is a magazine front, not a stat dashboard.)
//   6. Plan status (current Free / Stats / Reports)

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useParentApi } from '../api/parent';
import { useKidApi } from '../api/kid';
import { useMatchApi } from '../api/match';
import { usePlanLimitsApi } from '../api/stats';
import { useStripeApi } from '../api/stripe';
import { Bleed, Section } from '../components/Container';
import { Button } from '../components/Button';
import { OnboardingCard } from '../components/OnboardingCard';
import { FamilyShareCard, shouldShowFamilyShareCard } from '../components/FamilyShareCard';
import { OnThisDayWidget } from '../components/OnThisDayWidget';
import { WelcomeWalkthrough } from '../components/WelcomeWalkthrough';
import { MatchCard } from '../components/MatchCard';
import { KidAvatar } from '../components/KidAvatar';
import { EmptyState } from '../components/EmptyState';
import { IconPlus } from '../components/icons';
import type { Parent, Kid, Match, PlanLimits } from '../types';

function planLabel(p: Parent['plan']): string {
  if (p === 'reports') return 'Reports';
  if (p === 'stats') return 'Stats';
  return 'Free';
}

function issueLine(): string {
  const now = new Date();
  const m = now.toLocaleString('en-GB', { month: 'long' }).toUpperCase();
  const y = now.getFullYear();
  return `VOLUME I · ${m} ${y}`;
}

export function Dashboard() {
  const { user } = useAuth();
  const parentApi = useParentApi();
  const kidApi = useKidApi();
  const matchApi = useMatchApi();
  const planLimitsApi = usePlanLimitsApi();
  const stripeApi = useStripeApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openingPortal, setOpeningPortal] = useState(false);

  /**
   * One-click jump to Stripe's hosted billing portal for paid owners.
   * Calls our existing /stripe/portal endpoint (which mints a fresh
   * session URL) and redirects. Failure case: silently leave the user
   * on the dashboard — billing fixes are still reachable via /upgrade.
   */
  const openBillingPortal = async () => {
    if (openingPortal) return;
    setOpeningPortal(true);
    try {
      const { url } = await stripeApi.portal();
      window.location.href = url;
    } catch {
      // Reset so the user can retry; the /upgrade page has the same
      // button so it's never an end-state failure.
      setOpeningPortal(false);
    }
  };

  // ── Stripe success landing ────────────────────────────────────────────
  // Stripe redirects to /dashboard?subscribed=1 after a successful checkout.
  // We surface a thank-you banner once and clear the URL param so it
  // doesn't reappear on refresh. The webhook does the actual plan flip;
  // this is purely a UX courtesy.
  const justSubscribed = searchParams.get('subscribed') === '1';
  const justJoined = searchParams.get('joined') === '1';
  useEffect(() => {
    if (justSubscribed || justJoined) {
      // Strip the query param without leaving a back-button entry.
      const next = new URLSearchParams(searchParams);
      if (justSubscribed) next.delete('subscribed');
      if (justJoined) next.delete('joined');
      setSearchParams(next, { replace: true });
    }
  }, [justSubscribed, justJoined, searchParams, setSearchParams]);
  const [bannerVisible, setBannerVisible] = useState(justSubscribed);
  const [joinedBannerVisible, setJoinedBannerVisible] = useState(justJoined);

  const [parent, setParent] = useState<Parent | null>(null);
  const [kids, setKids] = useState<Kid[] | null>(null);
  const [topMatch, setTopMatch] = useState<Match | null>(null);
  const [topMatchKid, setTopMatchKid] = useState<Kid | null>(null);
  const [topMatchPhotoUrl, setTopMatchPhotoUrl] = useState<string | null>(null);
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  // seasonGoals + seasonMom dropped 2026-05-19 with the home-page tiles.
  // kidId → signed photo URL. Populated lazily after kids load so the
  // avatar component can render actual profile photos instead of falling
  // back to initials. Bounded by the per-plan kid cap (1 / 5 / 5).
  const [kidPhotoUrls, setKidPhotoUrls] = useState<Record<string, string>>({});
  // H1.1 — "On this day" widget data. null until the lookup returns;
  // if the lookup itself returns null (no match), the widget never renders.
  const [onThisDay, setOnThisDay] = useState<{
    matchId: string; kidId: string; kidName: string;
    date: string; yearsAgo: number; summary: string;
  } | null>(null);

  // Load parent + kids + plan-limits
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, ks, pl] = await Promise.all([
          parentApi.getMe(),
          kidApi.list(),
          planLimitsApi.get(),
        ]);
        if (cancelled) return;
        setParent(p);
        setKids(Array.isArray(ks) ? ks : []);
        setLimits(pl);
      } catch {
        if (!cancelled) { setParent(null); setKids([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [parentApi, kidApi, planLimitsApi]);

  // H1.1 — independent fetch, no spinner, silent failure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await parentApi.onThisDay();
        if (!cancelled && r) setOnThisDay(r);
      } catch { /* widget just doesn't appear */ }
    })();
    return () => { cancelled = true; };
  }, [parentApi]);

  // Once kids are known, fetch their profile photo URLs in parallel so the
  // KidAvatar component on the kids strip can render the actual photo
  // instead of falling back to initials. Free tier caps at 1 kid, paid at
  // 5 — so this is at most 5 S3 presign signs per dashboard load.
  useEffect(() => {
    if (!kids || kids.length === 0) return;
    let cancelled = false;
    (async () => {
      const withPhotos = kids.filter((k) => !!k.photoPath);
      if (withPhotos.length === 0) return;
      const entries = await Promise.all(
        withPhotos.map(async (k) => {
          try {
            const r = await kidApi.photoUrl(k.kidId);
            return [k.kidId, r.url] as const;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const e of entries) if (e) map[e[0]] = e[1];
      setKidPhotoUrls(map);
    })();
    return () => { cancelled = true; };
  }, [kids, kidApi]);

  // Once kids are known, fetch top-story match (latest across all kids).
  // 2026-05-19: the season-aggregate roll-up was removed from the dashboard
  // (it duplicated KidDetail's stat tiles and made the home page feel like
  // a kid page). Per-kid context belongs on the per-kid page.
  useEffect(() => {
    if (!kids || kids.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        // For multi-kid accounts, pull matches in parallel and pick the latest.
        const matchesPerKid = await Promise.all(
          kids.map((k) => matchApi.list(k.kidId).then((ms) => ({ kid: k, matches: ms })))
        );
        if (cancelled) return;

        // Pick the latest match across all kids by date desc
        let best: { kid: Kid; match: Match } | null = null;
        for (const { kid, matches } of matchesPerKid) {
          for (const m of matches) {
            if (m.deletedAt) continue;
            if (!best || m.date > best.match.date) best = { kid, match: m };
          }
        }
        if (!best) return;
        setTopMatch(best.match);
        setTopMatchKid(best.kid);

        // Lazy fetch photo URL for the top story (only if the match has one).
        // Multi-photo migration: imagePath (legacy) OR imagePaths[] (new). The
        // photo-url endpoint returns the primary photo regardless of which
        // field holds it, so we only need to gate the call.
        const hasAny =
          !!best.match.imagePath ||
          (Array.isArray(best.match.imagePaths) && best.match.imagePaths.length > 0);
        if (hasAny) {
          try {
            const res = await matchApi.photoUrl(best.kid.kidId, best.match.matchId);
            if (!cancelled) setTopMatchPhotoUrl(res.url);
          } catch { /* ok — fallback swatch will render */ }
        }
      } catch { /* fail-soft */ }
    })();
    return () => { cancelled = true; };
  }, [kids, matchApi]);

  const greeting = parent?.firstName || (user?.email?.split('@')[0] ?? '');

  return (
    <div>
      {/* ── 3-screen welcome walkthrough (first visit only) ─────────────── */}
      {parent && !parent.welcomeSeenAt ? (
        <WelcomeWalkthrough
          parent={parent}
          onDismissed={() => setParent({ ...parent, welcomeSeenAt: new Date().toISOString() })}
        />
      ) : null}

      {/* ── Co-parent household banner — quiet, persistent.
           Tells the viewer "you're sharing X's household" so it's never a
           surprise that the dashboard isn't strictly theirs. Owner sees
           nothing here. */}
      {parent?.household?.role === 'co_parent' ? (
        <div
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--green)',
            color: 'var(--ink)',
            padding: '10px 16px',
            borderRadius: 'var(--radius-md)',
            margin: '0 0 var(--space-base)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 'var(--small)',
          }}
        >
          <span style={{ fontSize: 16 }} aria-hidden>🤝</span>
          <span>
            You're sharing <strong>{parent.household.ownerEmail}</strong>'s
            MyFootballJournal.{' '}
            <Link to="/settings" style={{ color: 'var(--green)' }}>Manage</Link>
          </span>
        </div>
      ) : null}

      {/* ── Just-joined toast (one-shot, dismissible) ───────────────────── */}
      {joinedBannerVisible ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: 'linear-gradient(180deg, var(--green), var(--green-dark))',
            color: 'var(--ink-on-green)',
            padding: '12px 20px',
            borderRadius: 'var(--radius-md)',
            margin: '0 0 var(--space-base)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-masthead)',
                fontStyle: 'italic',
                fontSize: '1.125rem',
                textTransform: 'uppercase',
                letterSpacing: '-0.5px',
              }}
            >
              You're in the household
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 'var(--small)', opacity: 0.85 }}>
              You can record matches, upload photos, and edit kids just like the owner.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setJoinedBannerVisible(false)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: '1px solid rgba(250,245,236,0.5)',
              color: 'var(--ink-on-green)',
              borderRadius: 999,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 'var(--small)',
            }}
          >
            OK
          </button>
        </div>
      ) : null}

      {/* ── Subscription success banner (one-shot, dismissible) ─────────── */}
      {bannerVisible ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: 'linear-gradient(180deg, var(--green), var(--green-dark))',
            color: 'var(--ink-on-green)',
            padding: '12px 20px',
            borderRadius: 'var(--radius-md)',
            margin: '0 0 var(--space-base)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-masthead)',
                fontStyle: 'italic',
                fontSize: '1.125rem',
                textTransform: 'uppercase',
                letterSpacing: '-0.5px',
              }}
            >
              Welcome to the paid tier
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 'var(--small)', opacity: 0.85 }}>
              Your plan is updating in the background. Refresh in a few seconds if it doesn't show yet.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setBannerVisible(false)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: '1px solid rgba(250,245,236,0.5)',
              color: 'var(--ink-on-green)',
              borderRadius: 999,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 'var(--small)',
            }}
          >
            OK
          </button>
        </div>
      ) : null}

      {/* ── Masthead band ───────────────────────────────────────────────── */}
      <Bleed background="var(--surface-3)">
        <div style={{ color: 'var(--ink-on-green)', padding: '36px 24px 32px' }}>
          <div
            style={{
              fontFamily: 'var(--font-masthead)',
              fontStyle: 'italic',
              // Clamp lower than --display so the wordmark never overflows
              // a narrow viewport. "MyFootballJournal" is 17 chars long.
              fontSize: 'clamp(1.75rem, 7vw, 4rem)',
              lineHeight: 0.95,
              letterSpacing: '-0.01em',
              textTransform: 'uppercase',
              wordBreak: 'break-word',
            }}
          >
            MyFootballJournal
          </div>
          <div
            style={{
              marginTop: 6,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--micro)',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              opacity: 0.75,
            }}
          >
            {issueLine()}{greeting ? ` · WELCOME BACK, ${greeting.toUpperCase()}` : ''}
          </div>
        </div>
      </Bleed>

      <div style={{ marginTop: 'var(--space-xl)' }}>
        {/* ── Onboarding card (auto-hides once dismissed) ──────────────── */}
        {parent && kids !== null ? (
          <OnboardingCard
            parent={parent}
            kids={kids}
            onDismissed={() => setParent({ ...parent, onboardedAt: new Date().toISOString() })}
          />
        ) : null}

        {/* ── Family-share discovery (Stats/Reports owners only — H1.3) ── */}
        {parent && shouldShowFamilyShareCard(parent) ? (
          <FamilyShareCard
            parent={parent}
            onDismissed={() => setParent({ ...parent, familyShareSeenAt: new Date().toISOString() })}
          />
        ) : null}

        {/* ── On this day (H1.1) ────────────────────────────────────────── */}
        {onThisDay ? <OnThisDayWidget data={onThisDay} /> : null}

        {/* ── Top story ───────────────────────────────────────────────── */}
        {kids === null ? (
          <Section spacing="lg">
            <p style={{ color: 'var(--muted)' }}>Loading…</p>
          </Section>
        ) : kids.length === 0 ? (
          <Section spacing="lg">
            <EmptyState
              emoji="⚽"
              title="Start with the player"
              body="Add your first child and we'll set up their journal."
              cta={
                <Link to="/kids/new" style={{ textDecoration: 'none' }}>
                  <Button label="Add a child" />
                </Link>
              }
            />
          </Section>
        ) : topMatch && topMatchKid ? (
          <Section spacing="lg">
            <div style={{ marginBottom: 'var(--space-sm)' }}>
              <span
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--micro)',
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--gold)',
                  fontWeight: 600,
                }}
              >
                Top story · {topMatchKid.name}
              </span>
            </div>
            <MatchCard
              match={topMatch}
              photoUrl={topMatchPhotoUrl}
              variant="featured"
              to={`/kids/${encodeURIComponent(topMatchKid.kidId)}/matches/${encodeURIComponent(topMatch.matchId)}`}
            />
          </Section>
        ) : (
          <Section spacing="lg">
            <EmptyState
              emoji="📓"
              title="The pitch is still"
              body="No matches recorded yet. Bring your phone to the next match — recording lives in the app."
              cta={
                kids[0] ? (
                  <Link to={`/kids/${encodeURIComponent(kids[0].kidId)}/matches/new`} style={{ textDecoration: 'none' }}>
                    <Button label="Record a match" />
                  </Link>
                ) : null
              }
            />
          </Section>
        )}

        {/* ── Kids strip ──────────────────────────────────────────────── */}
        {kids && kids.length > 0 ? (
          <Section spacing="lg">
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--micro)',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              marginBottom: 'var(--space-sm)',
            }}>
              Your players
            </div>
            <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
              {kids.map((k) => (
                <Link
                  key={k.kidId}
                  to={`/kids/${encodeURIComponent(k.kidId)}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    textDecoration: 'none',
                    color: 'var(--ink)',
                    flexShrink: 0,
                    width: 88,
                  }}
                >
                  <KidAvatar
                    name={k.name}
                    photoUrl={kidPhotoUrls[k.kidId]}
                    kitColour={k.kitColour}
                    squadNumber={k.squadNumber}
                    size="md"
                  />
                  <span style={{ fontSize: 'var(--small)', fontWeight: 500 }}>{k.name}</span>
                </Link>
              ))}
              {/* "Add" tile — same dashed-circle "+" shape regardless of cap
                  state. At the plan kid-cap, a small gold "UPGRADE" tag
                  overlays the bottom of the circle and the link routes to
                  /upgrade instead of /kids/new. Keeps visual continuity
                  with the rest of the strip; the cap signal lives on the
                  badge, not in a redesigned tile. */}
              {(() => {
                const atKidCap = !!limits && limits.kidsUsed >= limits.kidsLimit;
                return (
                  <Link
                    to={atKidCap ? '/upgrade' : '/kids/new'}
                    aria-label={
                      atKidCap
                        ? `Upgrade — ${limits!.kidsLimit} ${limits!.kidsLimit === 1 ? 'child' : 'children'} on your plan`
                        : 'Add a child'
                    }
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      width: 88,
                      flexShrink: 0,
                      textDecoration: 'none',
                      color: 'var(--muted)',
                    }}
                  >
                    <span
                      style={{
                        width: 72, height: 72,
                        borderRadius: '50%',
                        border: '2px dashed var(--divider)',
                        display: 'grid', placeItems: 'center',
                        position: 'relative',
                      }}
                    >
                      <IconPlus size={28} />
                      {atKidCap ? (
                        <span
                          // Gold "UPGRADE" tag — overlapping bottom-centre
                          // of the dashed circle. Keeps the "+" tile shape
                          // intact (per user feedback 2026-05-19); the badge
                          // is the new signal.
                          style={{
                            position: 'absolute',
                            bottom: -8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'var(--gold)',
                            color: 'var(--ink-on-green)',
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding: '2px 8px',
                            borderRadius: 999,
                            whiteSpace: 'nowrap',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }}
                        >
                          Upgrade
                        </span>
                      ) : null}
                    </span>
                    <span style={{ fontSize: 'var(--small)' }}>
                      {atKidCap ? 'Upgrade' : 'Add'}
                    </span>
                  </Link>
                );
              })()}
            </div>
          </Section>
        ) : null}

        {/* "This season so far" stat tiles removed 2026-05-19. They lived
            on the home page since the dashboard rebuild but made the home
            look too much like KidDetail — the same three-tile pattern is
            on the per-kid Stats screen where it belongs. Per-kid context
            beats aggregated context on the home, especially with multi-kid
            households where aggregates blur whose journal you're reading. */}

        {/* ── Plan status ─────────────────────────────────────────────── */}
        {parent ? (
          <Section spacing="lg">
            <div
              style={{
                padding: 'var(--space-lg)',
                borderRadius: 'var(--radius-lg)',
                background: parent.plan === 'free' ? 'var(--surface-3)' : 'linear-gradient(180deg, var(--surface-3), var(--green-dark))',
                color: 'var(--ink-on-green)',
                boxShadow: 'var(--shadow-2)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <div
                  style={{
                    fontFamily: 'var(--font-masthead)',
                    fontStyle: 'italic',
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Your plan · {planLabel(parent.plan)}
                </div>
                {/* Paid owners get a one-tap jump to Stripe's billing portal
                    (change plan, update card, cancel). Co-parents on a paid
                    household see a link to /settings because they don't manage
                    billing. Free users see "See plans →" pointing to /upgrade. */}
                {parent.plan === 'free' ? (
                  <Link to="/upgrade" style={{ color: 'var(--gold-light)', fontSize: 'var(--small)' }}>
                    See plans →
                  </Link>
                ) : parent.household?.role === 'co_parent' ? (
                  <Link to="/settings" style={{ color: 'var(--gold-light)', fontSize: 'var(--small)' }}>
                    Manage
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => void openBillingPortal()}
                    disabled={openingPortal}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--gold-light)',
                      fontSize: 'var(--small)',
                      cursor: openingPortal ? 'progress' : 'pointer',
                      padding: 0,
                      fontFamily: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    {openingPortal ? 'Opening…' : 'Manage subscription →'}
                  </button>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 'var(--small)', opacity: 0.85 }}>
                {parent.plan === 'free'
                  ? `${limits?.matchesLimit ?? 10} matches per season, 1 child, 1 photo per match. Upgrade for more.`
                  : 'Thanks for supporting MyFootballJournal.'}
              </p>
            </div>
          </Section>
        ) : null}
      </div>
    </div>
  );
}
